<template>
  <v-card class="mb-4" elevation="3" rounded="xl">
    <v-row no-gutters class="align-stretch">
      <!-- Image -->
      <v-col cols="12" md="5" class="pa-0">
        <v-img :src="cruise.full_image_url || cruise.imgSrc" alt="Ship" cover class="cover-img rounded-img-resp">
          <!-- Overlay scrim -->
          <div class="img-scrim"></div>

          <!-- Nights badge -->
          <div class="img-badge" v-if="numberOfNights">
            <div class="img-badge-box">
              {{ numberOfNights }} Nights
            </div>
          </div>

          <!-- Big title on image -->
          <div class="img-title text-uppercase">
            {{ cruiseTitle }}
          </div>
        </v-img>
      </v-col>

      <!-- Content -->
      <v-col cols="12" md="7" class="pa-4 d-flex flex-column justify-space-between">
        <div>

          <!-- Favorite (top-right) -->
          <div class="img-actions">
            <v-btn icon variant="elevated" class="fav-btn" :loading="favBusy" @click.stop="toggleFavorite"
              :aria-label="isFav ? 'Remove from favorites' : 'Add to favorites'">
              <v-icon :class="{ 'text-error': isFav }">
                {{ isFav ? 'mdi-heart' : 'mdi-heart-outline' }}
              </v-icon>
            </v-btn>
          </div>


          <!-- Ship & Brand line -->
          <div class="ship-line mb-3">
            <div class="ship-icon">
              <v-icon size="30">mdi-ferry</v-icon>
            </div>

            <div class="ship-text">
              <div class="text-h6">{{ shipName }}</div>
              <div class="text-subtitle-2 text-medium-emphasis">{{ shipBrand }}</div>
            </div>
          </div>

          <!-- Route summary line -->
          <v-row class="ma-1">
            <div class="text-body-1 font-weight-bold text-uppercase mr-1">
              {{ routeType }}
            </div>
            <div class="text-body-1 ">
              {{ routeLabel }}
            </div>
          </v-row>

          <!-- Route summary line -->
          <v-row class="ma-1">
            <div class="text-body-1 font-weight-bold text-uppercase mr-1">
              Sailing On:
            </div>
            <div class="text-body-1 ">
              {{ startDateStr }}
            </div>
          </v-row>

          <!-- Visiting line -->
          <div class="text-body-1 ma-1">
            <span class="font-weight-bold text-uppercase">Visiting:</span>
            <span class="ml-1 text-medium-emphasis">{{ visitingText }}</span>
          </div>

          <!-- Room Types Table -->
          <v-table density="comfortable" class="mt-3 text-center">
            <thead>
              <tr>
                <th class="text-center text-body-1 text-medium-emphasis">INSIDE</th>
                <th class="text-center text-body-1 text-medium-emphasis">OUTSIDE</th>
                <th class="text-center text-body-1 text-medium-emphasis">BALCONY</th>
                <th class="text-center text-body-1 text-medium-emphasis">SUITE</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-body-1">{{ formatPrice(pricing.inside) }}</td>
                <td class="text-body-1">{{ formatPrice(pricing.outside) }}</td>
                <td class="text-body-1">{{ formatPrice(pricing.balcony) }}</td>
                <td class="text-body-1">{{ formatPrice(pricing.suite) }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>

        <v-btn @click="handleBookDeal(cruise.id)" color="primary" block class="mt-2 font-weight-bold">
          Book This Deal
        </v-btn>
        <v-btn @click="handleViewPorts()" variant="outlined" color="primary" block class="mt-2 font-weight-bold">
          View Itinerary
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="loginDialog" max-width="600">
      <v-card>
        <v-card-title>
          <div class="d-flex align-center">
            <v-icon class="me-2" style="color:var(--color-primary)">mdi-map</v-icon>
            <span class="text-h6 font-weight-bold">Itinerary</span>
          </div>
        </v-card-title>
        <v-card-text>
          <!-- Timeline -->
          <div class="timeline">
            <div v-for="(stop, i) in fullPortSchedule" :key="i" class="timeline-item">
              <div class="timeline-dot dot-port" />
              <div v-if="i !== fullPortSchedule.length - 1" class="timeline-line" />

              <div class="timeline-content">
                <div class="text-subtitle-2 font-weight-bold mb-1">
                  Day {{ i + 1 }}:
                  <span v-if="stop.seaDay">
                    <v-icon size="16" class="me-1" style="color:var(--color-primary)">mdi-waves</v-icon>
                    At Sea
                  </span>
                  <span v-else>{{ stop.port }}</span>
                </div>
                <div class="text-caption text-grey">{{ stop.date }}</div>
              </div>
            </div>

            <v-alert type="info" color="secondary" variant="text" density="comfortable" class="mt-2">
              Arrival and departure times are approximate and may vary. If you notice any
              discrepancies or errors, please report them so our team can update the itinerary.
            </v-alert>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="loginDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script>
import { supabase } from '@/supabaseClient'
import { mapState } from 'vuex'

export default {
  name: 'CruiseListingCard',
  props: {
    cruise: { type: Object, required: true }
  },
  data() {
    return {
      pricing: { inside: null, outside: null, balcony: null, suite: null },
      ship: null,
      loginDialog: false,
      // favorites
      isFav: false,
      favBusy: false,
    }
  },
  computed: {
    ...mapState(['user']), // <-- pull user from Vuex root state
    numberOfNights() {
      const title = this.cruise?.title || ''
      const match = title.match(/(\d+)\s*days?/i)
      return match ? parseInt(match[1]) : null
    },
    shipName() {
      // prefer fetched ship; fall back to cruise record
      return (this.ship && (this.ship.ship_name || this.ship.name))
        || this.cruise?.ship_name
        || '—'
    },
    shipBrand() {
      // prefer fetched ship; fall back to cruise record
      return (this.ship && (this.ship.cruise_line || this.ship.brand))
        || this.cruise?.cruise_line
        || this.cruise?.brand
        || '—'
    },
    portStops() {
      const p = this.cruise?.itinerary?.ports || this.cruise?.ports || []
      return Array.isArray(p) ? p : []
    },

    routeLabel() {
      const stops = this.portStops
      if (!stops.length) {
        return '' // nothing we can show
      }

      const first = stops[0]
      const last = stops[stops.length - 1]
      const isRoundtrip = this.sameStop(first, last)

      const startStr = this.formatPort(first)
      const endStr = this.formatPort(last)

      return isRoundtrip
        ? `${startStr}`
        : `${startStr} to ${endStr}`
    },
    routeType() {
      const stops = this.portStops
      if (!stops.length) {
        return '' // nothing we can show
      }

      const first = stops[0]
      const last = stops[stops.length - 1]
      const isRoundtrip = this.sameStop(first, last)

      return isRoundtrip
        ? `Roundtrip From: `
        : `Oneway From: `
    },
    fullPortSchedule() {
      return this.fillSeaDays(this.portStops ?? [])
    },
    startDateStr() {
      const ymd = this.cruise?.date
      if (!ymd) return "-";

      const [y, m, d] = ymd.split('-').map(Number);
      const date = new Date(y, m - 1, d); // local time, no timezone shift

      return date
        ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '';
    },
    visitingText() {
      const stops = Array.isArray(this.portStops) ? this.portStops : []
      const names = stops
        .map(s => this.formatPort(s))
        .filter(Boolean)
        // collapse consecutive duplicates like A • A -> A
        .filter((v, i, arr) => i === 0 || v !== arr[i - 1])

      return names.join(' • ')
    },
    cruiseTitle() {
      const s = this.cruise?.title
      if(!s) return ""

      const m = s.match(/^(.*?\btrip\b)(.*)$/i);
      if (m) return m[2].trim();
      return s.trim(); // if "trip" not found
    }
  },
  methods: {
    formatPrice(val) {
      if (val == null) return '—'
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
    },
    async fetchPricing() {
      const sailingID = this.cruise?.id
      if (!sailingID) return

      try {
        const { data: listings, error } = await supabase
          .from('listings')
          .select('room_type, total_cost')
          .eq('sailing_id', sailingID)

        if (error) {
          console.error('Error fetching listings:', error)
          return
        }

        const roomPrices = {
          inside: Infinity,
          outside: Infinity,
          balcony: Infinity,
          suite: Infinity
        }

          ; (listings || []).forEach(l => {
            const roomType = l.room_type?.toLowerCase()
            const cost = l.total_cost
            if (!roomType || cost == null) return
            if (roomType in roomPrices && cost < roomPrices[roomType]) {
              roomPrices[roomType] = cost
            }
          })

        this.pricing = {
          inside: roomPrices.inside === Infinity ? null : roomPrices.inside,
          outside: roomPrices.outside === Infinity ? null : roomPrices.outside,
          balcony: roomPrices.balcony === Infinity ? null : roomPrices.balcony,
          suite: roomPrices.suite === Infinity ? null : roomPrices.suite
        }
      } catch (err) {
        console.error('Unexpected error fetching pricing:', err)
      }
    },
    async fetchShip() {
      const shipID = this.cruise?.ship_id
      if (!shipID) return
      try {
        const { data, error } = await supabase
          .from('ships')
          .select('ship_id, ship_name, cruise_line')
          .eq('ship_id', shipID)
          .single()
        if (error) throw error
        this.ship = data
      } catch (err) {
        console.error('Error fetching ship:', err)
        this.ship = null
      }
    },
    // Collapse "a, b, c, ... , z" -> "a, z"
    condenseCommaParts(str = '') {
      const parts = String(str).split(',').map(s => s.trim()).filter(Boolean)
      if (parts.length >= 3) return `${parts[0]}, ${parts[parts.length - 1]}`
      if (parts.length === 2) return `${parts[0]}, ${parts[1]}`
      return parts[0] || ''
    },

    formatLocationStr(str = '') {
      return this.condenseCommaParts(str)
    },

    formatPort(stop = {}) {
      // If you ever get a prebuilt string like "Fort Lauderdale, Port Everglades, Florida"
      // just condense it and return.
      if (typeof stop === 'string') return this.condenseCommaParts(stop)
      if (stop.display) return this.condenseCommaParts(stop.display)

      const city = stop.city || stop.port_city || ''
      const portName = stop.port || stop.name || ''
      const region = stop.state || stop.region || stop.province || ''
      const country = stop.country || ''

      let out = ''
      // Prefer "City (Port), Region" or "City, Region"
      if (city) {
        const right = region || country
        out = portName ? `${city} (${portName})${right ? `, ${right}` : ''}`
          : `${city}${right ? `, ${right}` : ''}`
      } else {
        // Fallbacks when we only have names/country
        if (portName && country) out = `${portName}, ${country}`
        else out = portName || country || ''
      }

      return this.condenseCommaParts(out)
    },

    sameStop(a = {}, b = {}) {
      const codeA = (a.code || a.port_code || a.id || '').toString().toLowerCase()
      const codeB = (b.code || b.port_code || b.id || '').toString().toLowerCase()
      if (codeA && codeB) return codeA === codeB

      const na = this._normalize([a.city, a.port_city, a.port, a.name, a.state, a.region, a.province, a.country].filter(Boolean).join(', '))
      const nb = this._normalize([b.city, b.port_city, b.port, b.name, b.state, b.region, b.province, b.country].filter(Boolean).join(', '))

      return na === nb
    },

    _normalize(s = '') {
      return String(s).trim().toLowerCase().replace(/\s+/g, ' ')
    },

    fillSeaDays(portStops) {
      const result = []
      const year = '2025' // adjust if you store year elsewhere

      const parseDate = (dateStr) => {
        // Handles "09 Apr" or "09 Apr 10:00 - 10 Apr 18:00"
        const left = dateStr.split('-')[0].trim()
        const [day, mon] = left.split(' ')
        return new Date(`${day} ${mon} ${year}`)
      }

      for (let i = 0; i < portStops.length; i++) {
        const cur = portStops[i]
        const nxt = portStops[i + 1]
        const curDate = parseDate(cur.date)

        result.push({ ...cur, seaDay: false })

        if (nxt) {
          const nextDate = parseDate(nxt.date)
          const diffDays = Math.round((nextDate - curDate) / (1000 * 60 * 60 * 24))
          for (let j = 1; j < diffDays; j++) {
            const d = new Date(curDate)
            d.setDate(curDate.getDate() + j)
            const formatted = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
            result.push({ date: formatted, port: 'At Sea', seaDay: true })
          }
        }
      }
      return result
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    // --- FAVORITES ---
    async loadFavorite() {
      try {
        const uid = this.user?.uid || this.user?.id
        const cruiseId = this.cruise?.id
        if (!uid || !cruiseId) { this.isFav = false; return }

        // Try a dedicated favorites table: favorites(user_id, sailing_id)
        const { data, error } = await supabase
          .from('favorites')
          .select('id')
          .eq('user_id', uid)
          .eq('sailing_id', cruiseId)
          .limit(1)

        if (error) throw error
        this.isFav = Array.isArray(data) && data.length > 0
      } catch (e) {
        console.warn('loadFavorite:', e?.message || e)
        this.isFav = false
      }
    },

    async toggleFavorite() {
      if (!this.user) {
        this.loginDialog = true
        return
      }
      if (this.favBusy) return
      this.favBusy = true
      try {
        const uid = this.user?.uid || this.user?.id
        const cruiseId = this.cruise?.id
        if (!uid || !cruiseId) return

        if (this.isFav) {
          // remove
          const { error } = await supabase
            .from('favorites')
            .delete()
            .eq('user_id', uid)
            .eq('sailing_id', cruiseId)
          if (error) throw error
          this.isFav = false
        } else {
          // add
          const { error } = await supabase
            .from('favorites')
            .insert([{ user_id: uid, sailing_id: cruiseId, created_at: new Date().toISOString() }])
          if (error) throw error
          this.isFav = true
        }
      } catch (e) {
        console.error('toggleFavorite:', e?.message || e)
      } finally {
        this.favBusy = false
      }
    },

    handleBookDeal(id) {
      /*
      if (!this.user) {
        // Not logged in → show alert or modal
        this.loginDialog = true
        return
      }
        */

      this.$router.push(`/deals/listing/${id}`)
    },
    handleViewPorts() {
      this.loginDialog = true
    },
    goToLogin() {
      this.loginDialog = false
      this.$router.push({ name: 'Sign Up', query: { redirect: this.$route.fullPath } })
    }
  },
  watch: {
    cruise: {
      handler() {
        this.fetchPricing()
        this.fetchShip()
        this.loadFavorite()
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
/* Image height on mobile; fills column on md+ */
.cover-img {
  height: 220px;
  position: relative;
  /* needed for absolute overlays */
}

@media (min-width: 960px) {
  .cover-img {
    height: 100%;
  }
}

/* Dark-to-transparent bottom scrim like the screenshot */
.img-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
      rgba(0, 0, 0, 1.0) 0%,
      rgba(0, 0, 0, 0.55) 45%,
      rgba(0, 0, 0, 0.15) 85%);
  pointer-events: none;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
}

/* Nights badge (top-left) */
.img-badge {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 1;
}

.img-badge-box {
  background: #fff;
  color: #111;
  font-weight: 700;
  font-size: 20px;
  line-height: 1;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, .08);
  border-radius: 4px;
  /* make it a box; set to 0 for sharp corners */
  box-shadow: 0 2px 6px rgba(0, 0, 0, .12);
}

/* Big white title (bottom-left) */
.img-title {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 20px;
  color: #fff;
  font-weight: 800;
  line-height: 1.25;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.55);
  /* Responsive size similar to your screenshot */
  font-size: clamp(20px, 2.4vw, 30px);
}

/* Match brand accent on icons */
.text-secondary {
  color: var(--color-secondary);
}

/* Keep the rounded corners consistent on md+ */
.rounded-md-l-xl {
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
}

.ship-line {
  display: flex;
  align-items: center;
  /* center icon to the text block */
}

.ship-icon {
  display: flex;
  align-items: center;
  /* centers the <v-icon> itself */
  justify-content: center;
  margin-right: 12px;
  /* like mr-3 */
}

.ship-icon .v-icon {
  line-height: 1;
  /* avoid baseline wobble */
  vertical-align: middle;
  transform: translateY(1px);
}

.ship-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* centers the two lines as a block */
  min-width: 0;
}

/* Timeline */
.timeline {
  position: relative;
  padding-left: 28px;
}

.timeline-item {
  position: relative;
  padding: 10px;
}

.timeline-dot {
  position: absolute;
  left: 7px;
  top: 16px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.dot-port {
  background: var(--color-primary, #26a69a);
}

.timeline-line {
  position: absolute;
  left: 16px;
  top: 26px;
  width: 2px;
  height: calc(100%);
  background: var(--color-primary, #26a69a);
  opacity: .35;
}

.timeline-content {
  margin-left: 28px;
}

.img-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  /* above scrim */
}

.fav-btn {
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .12);
  border-radius: 9999px;
}

/* Mobile: round both top corners */
.rounded-img-resp {
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
}

/* Desktop (md+): round only the left side, zero the right side */
@media (min-width: 960px) {
  .rounded-img-resp {
    border-bottom-left-radius: 20px;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  /* keep scrim in sync with the image shape */
  .img-scrim {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
  }
}
</style>
